import { Component, Output, EventEmitter, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoanApplicationItem, LoanApplicationUpdateDTO } from '../../loan-applications/loan-applications.interface';
import { LoanApplicationService } from '../../loan-applications/loan-applications.service';
import { UserLoginService } from 'src/app/login/userlogin.service';
import { ToastrService } from 'ngx-toastr';
import { UserKYCDTO } from '../../kyc/userkyc.interface';
import { LosStateService } from '../los-state.service';

@Component({
  selector: 'app-bo-review',
  templateUrl: './bo-review.component.html',
  styleUrls: ['./bo-review.component.css']
})
export class BoReviewComponent implements OnInit {
  @Output() next = new EventEmitter<void>();
  @Output() prev = new EventEmitter<void>();

  private loanApplicationService = inject(LoanApplicationService);
  private userLoginService = inject(UserLoginService);
  private losState = inject(LosStateService);
  private toastr = inject(ToastrService);
  private destroyRef = inject(DestroyRef);

  applicationData: LoanApplicationItem | null = null;
  userKycData: UserKYCDTO | null = null;

  boDecision = 'approve';
  boComment = '';
  approvedAmount: number = 0;
  interestRate: number = 0;
  tenureOverride: number = 0;
  overrideCode = '';

  isSubmitting = false;

  ngOnInit(): void {
    this.losState.applicationData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(app => {
        this.applicationData = app;
        if (app) {
          this.approvedAmount = app.requestedAmount || 0;
          this.tenureOverride = app.tenureMonths || 0;
        }
      });

    this.losState.userKycData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(kyc => this.userKycData = kyc);
  }

  submitDecision(): void {
    if (!this.applicationData) return;
    if (this.isSubmitting) return;

    if (this.boDecision === 'approve' && this.approvedAmount <= 0) {
      this.toastr.warning('Please enter a valid approved amount');
      return;
    }

    if (this.boDecision === 'decline' && !this.boComment.trim()) {
      this.toastr.warning('Please enter a rejection reason');
      return;
    }

    this.isSubmitting = true;
    const currentUser = this.userLoginService.User();

    const payload: LoanApplicationUpdateDTO = {
      applicationId: this.applicationData.applicationId,
      userId: this.applicationData.userId,
      productId: this.applicationData.productId,
      applicationStatus: this.boDecision === 'approve' ? 'Approved' : 'Rejected',
      requestedAmount: this.applicationData.requestedAmount,
      approvedAmount: this.boDecision === 'approve' ? this.approvedAmount : 0,
      tenureMonths: this.tenureOverride || this.applicationData.tenureMonths,
      applicationDate: this.applicationData.applicationDate,
      approvalDate: this.boDecision === 'approve' ? new Date() : this.applicationData.approvalDate,
      approvedBy: this.boDecision === 'approve' ? (currentUser?.userName || '') : (this.applicationData.approvedBy || ''),
      rejectionReason: this.boDecision === 'decline' ? this.boComment : (this.applicationData.rejectionReason || ''),
      rejectedBy: this.boDecision === 'decline' ? (currentUser?.userName || '') : (this.applicationData.rejectedBy || ''),
      isDisbursed: this.applicationData.isDisbursed,
      disbursedOn: this.applicationData.disbursedOn,
      disbursedAmount: this.applicationData.disbursedAmount,
      subUserId: this.applicationData.subUserId,
      businessId: this.applicationData.businessId,
      productCode: this.applicationData.productCode || '',
      prrn: this.applicationData.prrn,
      lmsApplicationName: this.applicationData.lmsApplicationName,
      otherDetail1: this.applicationData.otherDetail1,
      // Store ROI in otherDetail2
      otherDetail2: this.boDecision === 'approve' ? this.interestRate.toString() : (this.applicationData.otherDetail2 || ''),
      // Store tenure override in otherDetail3
      otherDetail3: this.boDecision === 'approve' ? this.tenureOverride.toString() : (this.applicationData.otherDetail3 || ''),
      otherDetail4: this.applicationData.otherDetail4,
      otherDetail5: this.applicationData.otherDetail5,
      otherDetail6: this.applicationData.otherDetail6,
      actionUser: currentUser?.userId?.toString() || '0',
      isActive: 1
    };

    this.loanApplicationService.LoanApplication_Update(payload).subscribe({
      next: (res) => {
        this.losState.patchApplication(res);
        const msg = this.boDecision === 'approve'
          ? 'Application approved successfully'
          : 'Application rejected';
        this.toastr.success(msg);
        this.isSubmitting = false;
        if (this.boDecision === 'approve') {
          this.next.emit();
        }
        // On rejection, stay on the same step
      },
      error: (err) => {
        this.toastr.error('Failed to record BO decision');
        console.error(err);
        this.isSubmitting = false;
      }
    });
  }
}
